import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import MainURL from '../../../../MainURL';
import Loading from '../../../../components/Loading';
import { userDataAtom } from '../../../../AtomStore';
import MypageMenu from '../../common/MypageMenu';
import '../../common/Mypage.scss';

type PostType = 'place' | 'casting' | 'review';

type PlacePost = {
  id: number;
  placeName: string;
  sort: string;
  region: string;
  location: string;
  isView: string | boolean | number | null;
  date: string;
};

type CastingPost = {
  id: number;
  name: string;
  sort: string;
  isView: string | boolean | number | null;
  date: string;
};

type ReviewPost = {
  id: number;
  title: string;
  date: string;
  views: number;
  images?: string;
};

type RetreatListItem =
  | { postType: 'place'; data: PlacePost }
  | { postType: 'casting'; data: CastingPost }
  | { postType: 'review'; data: ReviewPost };

const FILTERS: { id: 'all' | PostType; label: string }[] = [
  { id: 'all', label: '전체' },
  { id: 'place', label: '수련회 장소' },
  { id: 'casting', label: '수련회 강사' },
  { id: 'review', label: '수련회 리뷰' },
];

const isVisible = (value: unknown) =>
  value === true || value === 1 || value === '1' || value === 'true';

const getTitle = (item: RetreatListItem) => {
  if (item.postType === 'place') return item.data.placeName;
  if (item.postType === 'casting') return item.data.name;
  return item.data.title;
};

const getCategoryLabel = (postType: PostType) => {
  if (postType === 'place') return '수련회 장소';
  if (postType === 'casting') return '수련회 강사';
  return '수련회 리뷰';
};

const getDetailPath = (item: RetreatListItem) => {
  if (item.postType === 'place') return `/retreat/place/detail?id=${item.data.id}`;
  if (item.postType === 'casting') return `/retreat/casting/detail?id=${item.data.id}`;
  return `/retreat/review/detail?id=${item.data.id}`;
};

const getDeleteUrl = (postType: PostType) => {
  if (postType === 'place') return `${MainURL}/retreat/userdelete`;
  if (postType === 'casting') return `${MainURL}/retreatcasting/userdelete`;
  return `${MainURL}/retreatreview/postdelete`;
};

export default function RetreatPostingManage() {
  const navigate = useNavigate();
  const [userData] = useAtom(userDataAtom);
  const [filter, setFilter] = useState<'all' | PostType>('all');
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [placePosts, setPlacePosts] = useState<PlacePost[]>([]);
  const [castingPosts, setCastingPosts] = useState<CastingPost[]>([]);
  const [reviewPosts, setReviewPosts] = useState<ReviewPost[]>([]);

  const fetchPosts = async () => {
    if (!userData.userAccount) {
      setPlacePosts([]);
      setCastingPosts([]);
      setReviewPosts([]);
      return;
    }

    setLoading(true);
    try {
      const account = encodeURIComponent(userData.userAccount);
      const [placeRes, castingRes, reviewRes] = await Promise.all([
        axios.get(`${MainURL}/retreat/getuserposts/${account}`),
        axios.get(`${MainURL}/retreatcasting/getuserposts/${account}`),
        axios.get(`${MainURL}/retreatreview/getuserposts/${account}`),
      ]);

      setPlacePosts(Array.isArray(placeRes.data) ? placeRes.data : []);
      setCastingPosts(Array.isArray(castingRes.data) ? castingRes.data : []);
      setReviewPosts(Array.isArray(reviewRes.data) ? reviewRes.data : []);
    } catch (error) {
      console.error(error);
      setPlacePosts([]);
      setCastingPosts([]);
      setReviewPosts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchPosts();
  }, [userData.userAccount, refresh]);

  const allPosts = useMemo<RetreatListItem[]>(() => {
    const placeItems: RetreatListItem[] = placePosts.map((data) => ({ postType: 'place', data }));
    const castingItems: RetreatListItem[] = castingPosts.map((data) => ({ postType: 'casting', data }));
    const reviewItems: RetreatListItem[] = reviewPosts.map((data) => ({ postType: 'review', data }));

    return [...placeItems, ...castingItems, ...reviewItems].sort((a, b) => {
      const dateA = new Date(a.data.date).getTime();
      const dateB = new Date(b.data.date).getTime();
      return (Number.isNaN(dateB) ? 0 : dateB) - (Number.isNaN(dateA) ? 0 : dateA);
    });
  }, [placePosts, castingPosts, reviewPosts]);

  const filteredPosts = useMemo(() => {
    if (filter === 'all') return allPosts;
    return allPosts.filter((item) => item.postType === filter);
  }, [allPosts, filter]);

  const handleDelete = async (item: RetreatListItem) => {
    if (!window.confirm('정말로 이 게시글을 삭제하시겠습니까?')) return;

    try {
      const body =
        item.postType === 'review'
          ? {
              postId: item.data.id,
              userAccount: userData.userAccount,
              images: item.data.images,
            }
          : {
              id: item.data.id,
              userAccount: userData.userAccount,
            };

      const res = await axios.post(getDeleteUrl(item.postType), body);
      if (res.data) {
        alert('게시글이 삭제되었습니다.');
        setRefresh((prev) => !prev);
      } else {
        alert('삭제에 실패했습니다.');
      }
    } catch (error) {
      console.error(error);
      alert('삭제에 실패했습니다.');
    }
  };

  const handleEdit = (item: RetreatListItem) => {
    navigate(`/mypage/retreatpostingedit?type=${item.postType}&id=${item.data.id}`);
    window.scrollTo(0, 0);
  };

  const handleView = (item: RetreatListItem) => {
    window.open(getDetailPath(item), '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="mypage">
      <div className="inner">
        <MypageMenu />
        <div className="subpage__main">
          <div className="mypage-panel mypage-section-card">
            <div className="mypage-section-head">
              <div>
                <h2 className="mypage-section-title">수련회게시글 관리</h2>
                <p className="mypage-section-copy">
                  로그인 계정으로 등록한 수련회 장소·강사·리뷰 게시글을 수정하거나 삭제할 수 있습니다.
                </p>
              </div>
              <div className="mypage-filter-row">
                {FILTERS.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    className={`mypage-filter-chip ${filter === tab.id ? 'is-active' : ''}`}
                    onClick={() => setFilter(tab.id)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
                <Loading />
              </div>
            ) : filteredPosts.length > 0 ? (
              <div className="postingList">
                {filteredPosts.map((item) => (
                  <div key={`${item.postType}-${item.data.id}`} className="postingItem">
                    <div className="postingHeader">
                      <div className="postingTitle">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '5px' }}>
                          <span className="categoryTag">{getCategoryLabel(item.postType)}</span>
                          <h3 style={{ margin: 0 }}>{getTitle(item)}</h3>
                        </div>
                        <span className="postingDate">{item.data.date}</span>
                      </div>
                      <div className="postingActions">
                        <button className="actionBtn viewBtn" type="button" onClick={() => handleView(item)}>
                          보기
                        </button>
                        <button className="actionBtn editBtn" type="button" onClick={() => handleEdit(item)}>
                          수정
                        </button>
                        <button className="actionBtn deleteBtn" type="button" onClick={() => handleDelete(item)}>
                          삭제
                        </button>
                      </div>
                    </div>
                    <div className="postingInfo">
                      {item.postType === 'place' && (
                        <>
                          <div className="infoRow">
                            <span className="infoLabel">형식:</span>
                            <span className="infoValue">{item.data.sort}</span>
                          </div>
                          <div className="infoRow">
                            <span className="infoLabel">지역:</span>
                            <span className="infoValue">{item.data.region}</span>
                          </div>
                          <div className="infoRow">
                            <span className="infoLabel">노출:</span>
                            <span className="infoValue">{isVisible(item.data.isView) ? '노출' : '검토중'}</span>
                          </div>
                        </>
                      )}
                      {item.postType === 'casting' && (
                        <>
                          <div className="infoRow">
                            <span className="infoLabel">구분:</span>
                            <span className="infoValue">{item.data.sort}</span>
                          </div>
                          <div className="infoRow">
                            <span className="infoLabel">노출:</span>
                            <span className="infoValue">{isVisible(item.data.isView) ? '노출' : '검토중'}</span>
                          </div>
                        </>
                      )}
                      {item.postType === 'review' && (
                        <div className="infoRow">
                          <span className="infoLabel">조회수:</span>
                          <span className="infoValue">{item.data.views}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="noPosts">
                <p>작성한 수련회 게시글이 없습니다.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
